import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { IUserSession } from '../interfaces/IUserSession';
import { IdentityService } from '../services/identityService';
import { mapVerifyResponseToUserSession } from '../utils/sessionMapper';
import { clearAllEventDrafts } from './useEventDraft';
import axios, { AxiosError } from 'axios';
import config from '../config';
import { queueSessionLogout, flushSessionLogouts } from '../utils/sessionLogout';

const tryReadStoredSession = (): IUserSession | null => {
    const savedSession = localStorage.getItem('session');
    if (!savedSession || savedSession === 'undefined') {
        return null;
    }

    try {
        return JSON.parse(savedSession) as IUserSession;
    } catch {
        localStorage.removeItem('session');
        return null;
    }
};

export const useSessionManagement = () => {
    const [searchParams] = useSearchParams();
    const isPreviewMode = searchParams.get('preview') === 'true';

    const [session, setSessionState] = useState<IUserSession | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    const setSession = useCallback((newSession: IUserSession | null) => {
        if (!newSession) {
            try { queueSessionLogout(tryReadStoredSession()?.logoutToken); }
            catch { console.warn('Could not persist session logout'); }
            void flushSessionLogouts();
        }
        setSessionState(newSession);
        if (newSession) {
            localStorage.setItem('session', JSON.stringify(newSession));
        } else {
            localStorage.removeItem('session');
        }
    }, []);

    useEffect(() => {
        if (isPreviewMode) return;

        const syncSession = (event: StorageEvent) => {
            if (event.key !== 'session' && event.key !== null) return;

            const storedSession = localStorage.getItem('session');
            if (!storedSession) {
                if (session) {
                    clearAllEventDrafts();
                    setSessionState(null);
                    navigate('/');
                }
                return;
            }

            try {
                const sharedSession = JSON.parse(storedSession) as IUserSession;
                if (typeof sharedSession.token === 'string' && sharedSession.token) {
                    setSessionState(sharedSession);
                }
            } catch {
                // Ignore malformed cross-tab updates; the active tab still owns its current session.
            }
        };

        window.addEventListener('storage', syncSession);
        return () => window.removeEventListener('storage', syncSession);
    }, [isPreviewMode, navigate, session]);

    useEffect(() => {
        const retry = () => { void flushSessionLogouts(); };
        retry();
        window.addEventListener('online', retry);
        window.addEventListener('focus', retry);
        return () => {
            window.removeEventListener('online', retry);
            window.removeEventListener('focus', retry);
        };
    }, []);



    const signIn = useCallback(() => {
        navigate('/sign-in');
    }, [navigate]);

    const signOut = useCallback(() => {
        clearAllEventDrafts();
        setSession(null);
        navigate('/');
    }, [navigate, setSession]);

    const sessionContextValue = {
        session,
        setSession,
        signIn,
        signOut,
        isLoading
    };

    useEffect(() => {
        async function checkAuth() {
            // Skip auth check in preview mode
            if (isPreviewMode) {
                setIsLoading(false);
                return;
            }

            const parsedExistingSession = tryReadStoredSession();
            const token = parsedExistingSession?.token;

            if (!token) {
                setSessionState(null);
                setIsLoading(false);
                return;
            }

            const apiClient = axios.create({
                baseURL: config.managementBaseUrl,
                timeout: 10000,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                withCredentials: true,
            });

            const identityService = new IdentityService(apiClient);

            try {
                let activeToken = token;
                let logoutToken = parsedExistingSession?.logoutToken;

                // Refresh first so we avoid briefly treating an expired access token as a valid session.
                try {
                    const refreshResp = await axios.post(
                        `${config.managementBaseUrl}/users/refresh`,
                        null,
                        {
                            withCredentials: true,
                            timeout: 10000,
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        }
                    );

                    if (refreshResp.data?.token) {
                        activeToken = refreshResp.data.token;
                        logoutToken = refreshResp.data.logoutToken ?? logoutToken;
                    }
                } catch (refreshErr: unknown) {
                    if (tryReadStoredSession()?.token !== token) return;
                    const status = refreshErr instanceof AxiosError ? refreshErr.response?.status : undefined;
                    const isTerminal = status === 400 || status === 401 || status === 403;
                    if (isTerminal) {
                        // Server confirmed the session is invalid — clear and stop.
                        console.warn('[Auth] Startup refresh rejected by server, clearing session', { status });
                        setSession(null);
                        setIsLoading(false);
                        return;
                    }
                    // Transient failure — continue with the existing access token.
                    console.warn('[Auth] Startup refresh failed (transient), continuing with stored token', refreshErr instanceof Error ? refreshErr.message : String(refreshErr));
                }

                if (tryReadStoredSession()?.token !== token) return;
                apiClient.defaults.headers.common.Authorization = `Bearer ${activeToken}`;
                const user = await identityService.getCurrentUser();
                if (tryReadStoredSession()?.token !== token) return;
                if (user) {
                    const freshSession = mapVerifyResponseToUserSession(user);
                    const mergedSession: IUserSession = {
                        ...freshSession,
                        token: activeToken,
                        logoutToken,
                        configuration: {
                            ...freshSession.configuration,
                            ...(parsedExistingSession?.configuration && {
                                primaryColour: parsedExistingSession.configuration.primaryColour || freshSession.configuration.primaryColour,
                                secondaryColour: parsedExistingSession.configuration.secondaryColour || freshSession.configuration.secondaryColour,
                                mode: parsedExistingSession.configuration.mode || freshSession.configuration.mode,
                            })
                        }
                    };

                    if (tryReadStoredSession()?.token === token) setSession(mergedSession);
                } else {
                    setSession(null);
                }
            } catch (err: unknown) {
                if (tryReadStoredSession()?.token !== token) return;
                const status = err instanceof AxiosError ? err.response?.status : undefined;
                const isTerminal = status === 401 || status === 403;
                if (isTerminal) {
                    // Server confirmed the credentials are invalid.
                    setSession(null);
                } else {
                    // Transient failure (network down, timeout, 5xx) — restore the stored session
                    // so an offline user is not logged out. The 401 interceptor will handle
                    // re-authentication once connectivity is restored.
                    console.warn('[Auth] Startup getCurrentUser failed (transient), restoring stored session', err instanceof Error ? err.message : String(err));
                    setSession(parsedExistingSession);
                }
            } finally {
                setIsLoading(false);
            }
        }
        checkAuth();
    }, [isPreviewMode, setSession]);

    return { session, setSession, signIn, signOut, sessionContextValue, isLoading };
};
